import type { BetLinks, FairSource, LeagueId, PlayerIdentity, PromoKind, PropsBook, PropsPromo, PropsRow, SniperRow } from "./types";

export const SLUG = /^[a-z0-9][a-z0-9_-]{0,63}$/;
export const EVENT_ID = /^[A-Za-z0-9_.:-]{1,128}$/;
export const CELL_KEY = /^[A-Za-z0-9_-]{11}$/;
const utf8 = new TextEncoder();
export const isEntityKey = (value: string) => !/\p{Cc}/u.test(value) && utf8.encode(value).length <= 128;

export type BetLinksWire = string | readonly [string, string] | BetLinks;
export const isBetLinks = (links: unknown): links is BetLinksWire => typeof links === "string"
  || (Array.isArray(links) && (links.length === 2 || links.length === 3) && links.every((link) => typeof link === "string"));

export function expandLinks(links: BetLinksWire): BetLinks {
  if (typeof links === "string") return [links, links, links];
  return links.length === 2 ? [links[0], links[1], links[0]] : links;
}

export function playerOf(entity: { player_id?: string; image_url?: string; position?: string }): PlayerIdentity | null {
  if (!entity.player_id) return null;
  return { id: entity.player_id, imageUrl: entity.image_url || null, position: entity.position || null };
}

const FAIR_SOURCES: ReadonlyMap<string | undefined, FairSource> = new Map([["peer_consensus_ex_self", "market"], ["ladder_negbin", "model"], ["hybrid_negbin", "blend"]]);

function fairSourceOf(src: string | undefined, peers: number): FairSource {
  return FAIR_SOURCES.get(src) ?? (peers > 0 ? "market" : "model");
}

type WireRecord = { readonly [key: string]: unknown };
const isRecord = (value: unknown): value is WireRecord => typeof value === "object" && value !== null && !Array.isArray(value);
const isList = (value: unknown): value is readonly unknown[] => Array.isArray(value);
const isString = (value: unknown): value is string => typeof value === "string";
const isOptionalString = (value: unknown): value is string | undefined => value === undefined || typeof value === "string";
const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
export const isCount = (value: unknown): value is number => isNumber(value) && Number.isSafeInteger(value) && value >= 0;
const isPositive = (value: unknown): value is number => isNumber(value) && value > 0;
export const isOdds = (value: unknown): value is number => isNumber(value) && value > 1;
export const isUsd = (value: unknown): value is number => isNumber(value) && value >= 0;
export const isProbability = (value: unknown): value is number => isNumber(value) && value >= 0 && value <= 1;
const isSlug = (value: unknown): value is string => isString(value) && SLUG.test(value);
function orNull<T>(value: unknown, check: (value: unknown) => value is T): value is T | null {
  return value === null || check(value);
}
type OppLimits = { readonly [book: string]: number };
const isOppLimits = (value: unknown): value is OppLimits => isRecord(value) && Object.entries(value).every(([book, size]) => isSlug(book) && isUsd(size));

type RowLayout = { readonly id: RegExp; readonly entity: number };
const PROPS_ROW: RowLayout = { id: /^[^|]+\|[^|]*\|[^|]+\|[^|]+\|[^|]*(\|[^|]+)?$/, entity: 1 };
const SNIPER_ROW: RowLayout = { id: /^[^|]+\|[^|]+\|[^|]*\|[^|]+\|[^|]+\|[^|]*(\|[^|]+)?$/, entity: 2 };
const PROMO_KIND_BY_WIRE: ReadonlyMap<string, PromoKind> = new Map([
  ["gimme_pick", "gimme"], ["stat_slice", "slice"], ["boosted_multiplier", "power"], ["taco", "taco"], ["hotdog", "hotdog"], ["flash_sale", "flash"], ["free_square", "free"],
]);

export const UNKNOWN_KIND = "unknown_promo_kind";

function promoOf(promo: unknown): PropsPromo | typeof UNKNOWN_KIND | null {
  if (!isRecord(promo)) return null;
  const { type, name, cap, min_picks: minPicks, max_per_lineup: maxPerLineup, original_line: originalLine, original_multiplier: originalMultiplier } = promo;
  if (!isString(type) || !isString(name) || !orNull(cap, isUsd) || !isCount(minPicks) || !isCount(maxPerLineup)
    || !orNull(originalLine, isNumber) || !orNull(originalMultiplier, isPositive)) return null;
  const kind = PROMO_KIND_BY_WIRE.get(type);
  if (kind === undefined) return UNKNOWN_KIND;
  return { kind, name, cap, minPicks, maxPerLineup, originalLine, originalMultiplier };
}

function propsBooksOf(books: unknown, rowSelectionId: string, oppLimits: OppLimits): PropsBook[] | null {
  if (!isList(books)) return null;
  const parsed: PropsBook[] = [];
  for (const entry of books) {
    if (!isList(entry) || entry.length !== 9) return null;
    const [book, line, odds, oppOdds, fair, limit, selectionId, links, eligible] = entry;
    if (!isSlug(book) || !orNull(line, isNumber) || !isOdds(odds) || !orNull(oppOdds, isOdds) || !orNull(fair, isProbability)
      || !orNull(limit, isUsd) || !orNull(selectionId, isString) || !isBetLinks(links) || typeof eligible !== "boolean") return null;
    const oppLimit = Object.hasOwn(oppLimits, book) ? oppLimits[book] : null;
    parsed.push({ book, line, odds, oppOdds, fair, limit, oppLimit, selectionId: selectionId ?? rowSelectionId, links: expandLinks(links), eligible });
  }
  return parsed;
}

function bookRowOf<Extra extends object>(row: WireRecord, league: LeagueId, layout: RowLayout, extra: Extra): (PropsRow & Extra) | typeof UNKNOWN_KIND | null {
  const { id, event, market, entity, market_type: marketType, team, side, line, odds, limit, opp_limit: oppLimit, opp_limits: oppLimits, fair, src, ev, peers, sel, links, books, promo, pick, cell } = row;
  if (!isString(id) || !layout.id.test(id) || !isString(event) || !EVENT_ID.test(event) || !isString(market) || !isRecord(entity)) return null;
  const { key, name, player_id: playerId, image_url: imageUrl, position } = entity;
  if (!isString(key) || !isEntityKey(key) || !isString(name) || !isOptionalString(playerId) || !isOptionalString(imageUrl) || !isOptionalString(position)) return null;
  if (!(marketType === undefined || marketType === "game") || !isString(team) || !isString(side) || !orNull(line, isNumber)
    || !isList(odds) || odds.length !== 2 || !isList(sel) || sel.length !== 2) return null;
  const [price, oppPrice] = odds;
  const [selectionId, oppSelectionId] = sel;
  if (!isOdds(price) || !orNull(oppPrice, isOdds) || !orNull(limit, isUsd) || !orNull(oppLimit, isUsd) || !isOppLimits(oppLimits) || !isProbability(fair) || !isOptionalString(src) || !isNumber(ev) || !isCount(peers)
    || !isString(selectionId) || !isString(oppSelectionId) || !isBetLinks(links) || !(pick === undefined || (isString(pick) && pick !== ""))) return null;
  const parsedBooks = propsBooksOf(books, selectionId, oppLimits);
  const parsedPromo = promo === undefined ? null : promoOf(promo);
  if (parsedBooks === null || (promo !== undefined && parsedPromo === null) || !(cell === undefined || (isString(cell) && CELL_KEY.test(cell)))) return null;
  const parts = id.split("|");
  if (marketType !== "game" && (key === "" || parts[layout.entity] === "")) return null;
  if (parsedPromo === UNKNOWN_KIND) return UNKNOWN_KIND;
  return {
    id, eventId: event, league, marketType: marketType ?? "player", marketKey: parts[layout.entity + 1] ?? "", market, entity: { key, name },
    player: playerOf({ player_id: playerId, image_url: imageUrl, position }), team,
    side, line, odds: price, oppOdds: oppPrice, limit, oppLimit,
    fair, ev, peers, fairSource: fairSourceOf(src, peers), selectionId, oppSelectionId,
    links: expandLinks(links), books: parsedBooks, promo: parsedPromo, pick: pick ?? null, cell: cell ?? null, whales: null, ...extra,
  };
}

export function propsRowOf(row: unknown, league: LeagueId): PropsRow | typeof UNKNOWN_KIND | null {
  return isRecord(row) ? bookRowOf(row, league, PROPS_ROW, {}) : null;
}

export function sniperRowOf(row: unknown, league: LeagueId): SniperRow | typeof UNKNOWN_KIND | null {
  if (!isRecord(row)) return null;
  const { book } = row;
  return isSlug(book) ? bookRowOf(row, league, SNIPER_ROW, { book }) : null;
}
